import moment from 'moment';
import currency from 'currency.js';
import { CONTRACT_STATUS, CORRECTION_STATUS, REVIEW_STATUS, REVIEW_DISPLAY_SUBMITTED, REVIEW_DISPLAY_SS_REVIEWED, REVIEW_SUBMITTED_COLOR, REVIEW_STATUS_UI, SALARIO_MINIMO, UVT } from './constants';

export const debugLog = (...args) => (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') && console.log(...args); // eslint-disable-line
// Checks the role rather than a permission on purpose: permissions are a stored
// per-user snapshot taken at creation, so a permission added to ROLES.ADMIN
// later is absent from every existing admin's array. The role survives that.
export const isAdmin = (user) => Array.isArray(user?.roles) && user.roles.includes('ADMIN');
export const sortAsc = (key) => (a, b) => a[key] > b[key] ? 1 : a[key] < b[key] ? -1 : 0;
export const sortDesc = (key) => (a, b) => a[key] > b[key] ? -1 : a[key] < b[key] ? 1 : 0;
export const genString = (len = 16) => {
  let str = '';
  for (let i = 0; i < len; i++) {
    str += 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'.charAt(Math.floor(Math.random() * 62));
  }
  return str;
}
export const getAnnexUrl = (contractCode, annex) => {
  if (!contractCode || !annex?.id) return null;
  const { origin } = window.location;
  if (!origin) return null;
  if (annex.id.includes('.url')) {
    return annex.url;
  }
  return `${origin}/contract/${contractCode}/annexe_${annex.id}`;
}

// Días de un mes que caen dentro del rango [from, to], en la convención
// comercial del contrato: TODOS los meses tienen 30 días, así que febrero
// aporta 30 y el día 31 nunca cuenta. Devuelve el conjunto de días y no el
// total, porque las novedades se solapan y hay que saber CUÁLES días cubre
// cada una para no contarlos dos veces.
const monthDaysInRange = (monthMoment, from, to) => {
  const days = new Set();
  if (!from || !to) return days;

  const month = moment(monthMoment).startOf('month');
  const fromMoment = moment(from);
  const toMoment = moment(to);
  if (fromMoment.isAfter(month, 'month') || toMoment.isBefore(month, 'month')) return days;

  const firstDay = fromMoment.isSame(month, 'month') ? fromMoment.date() : 1;
  const lastDay = toMoment.isSame(month, 'month') ? Math.min(30, toMoment.date()) : 30;
  if (firstDay > 30) return days;

  for (let d = firstDay; d <= lastDay; d++) days.add(d);
  return days;
};

// Días comerciales que abarca el rango [from, to], ambos inclusive.
export const countContractDays = (from, to) => {
  if (!from || !to) return 0;

  let total = 0;
  const lastMonth = moment(to).startOf('month');
  const month = moment(from).startOf('month');
  while (month.isSameOrBefore(lastMonth, 'month')) {
    total += monthDaysInRange(month, from, to).size;
    month.add(1, 'month');
  }

  return total;
};

// Tarifa del último día con valor de un calendario de plan: la del tramo más
// reciente, que tras una prórroga no coincide con salary / 30.
const lastDailyRate = (planDays) => {
  for (let i = planDays.length - 1; i >= 0; i--) {
    const days = [...planDays[i].keys()].sort((x, y) => y - x);
    for (const day of days) {
      const rate = planDays[i].get(day);
      if (rate > 0) return rate;
    }
  }

  return 0;
};

// Fecha del último día de un rango de `days` días comerciales que arranca en
// `from` (inclusive), la operación inversa de monthDaysInRange. Un inicio en
// día 31 arranca el 1 del mes siguiente, porque el 31 no existe en la
// convención. Febrero no puede representar los días 29 y 30 comerciales, así
// que un rango que termina ahí se ancla en el último día real del mes.
export const addContractDays = (from, days) => {
  const total = Math.floor(Number(days) || 0);
  if (!from || total < 1) return null;

  let month = moment(from).startOf('month');
  let firstDay = moment(from).date();
  if (firstDay > 30) {
    month = month.add(1, 'month');
    firstDay = 1;
  }

  let remaining = total;
  while (remaining > 30 - firstDay + 1) {
    remaining -= 30 - firstDay + 1;
    month = month.add(1, 'month');
    firstDay = 1;
  }

  return month.date(Math.min(firstDay + remaining - 1, month.daysInMonth()));
};

// Construye el calendario vigente del plan de pagos aplicando la pila de
// novedades. Devuelve el plan mes a mes junto con el calendario por día que
// lo respalda: de ahí se derivan también el saldo a favor del contrato y la
// tarifa diaria vigente, que comparten esta única fuente de verdad.
//
// SOLO APLICA AL PLAN MENSUAL DE LOS CONTRATOS DE PERSONA NATURAL: proyecta el
// ÍNDICE de paymentPlan sobre los meses del contrato desde startedAt. El plan
// de los contratos jurídicos es manual y su índice identifica un HITO, no un
// mes, así que las claves que dependen de este calendario (paymentPlan,
// dailyRate, balance) se resuelven aparte en ContractLegalForm y nunca llegan
// aquí. Ver openspec/changes/legal-contract-news/design.md (Decisión 2).
const buildPaymentCalendar = (contract, news) => {
  const { startedAt, finishedAt, contractBudget, paymentPlan } = contract;
  if (!paymentPlan) return null;

  let adjustedPaymentPlan = [...paymentPlan];

  // Estado acumulado del contrato a medida que se aplican las novedades
  // en orden: cada adición debe evaluarse contra el presupuesto y la
  // fecha de fin vigentes (incluyendo adiciones anteriores), no contra
  // los valores originales del contrato.
  let planContractBudget = contractBudget;
  let planFinishedAt = finishedAt ? moment(finishedAt) : null;

  const contractStartMonth = moment(startedAt).startOf('month');
  const monthMomentAt = (idx) => contractStartMonth.clone().add(idx, 'months');
  const monthIndexAt = (date) => moment(date).startOf('month').diff(contractStartMonth, 'months');

  // Calendario vigente del plan: por cada mes, un Map de día del mes ->
  // tarifa de ese día, y el conjunto de días suspendidos. Las novedades no
  // se pueden resolver contra el paymentPlan original porque una prórroga
  // reprograma qué días cubre cada mes y a qué tarifa. La tarifa se guarda
  // por DÍA y no por mes porque una prórroga puede partir un mes en dos
  // tramos con tarifas distintas, y una suspensión posterior necesita saber
  // cuánto vale exactamente cada día que descuenta.
  const planDays = adjustedPaymentPlan.map((value, i) => {
    const days = monthDaysInRange(monthMomentAt(i), startedAt, finishedAt);
    const dailyPayment = days.size ? value / days.size : 0;
    return new Map([...days].map(day => [day, dailyPayment]));
  });
  const suspendedDays = adjustedPaymentPlan.map(() => new Set());
  // A qué prórroga pertenece cada día reprogramado (día -> id del tramo).
  // Los días del plan original no tienen entrada. Hace falta porque una
  // suspensión sobre un tramo ya prorrogado no elimina ese dinero: el plazo
  // está pactado hasta el final del tramo, así que lo suspendido se corre a
  // los días que quedan del mismo tramo.
  const dayExtension = adjustedPaymentPlan.map(() => new Map());
  // Presupuesto que cada prórroga puso sobre su tramo. Si al pactarla el
  // tramo estaba suspendido por completo no hay dónde colocarlo, así que el
  // saldo queda pendiente hasta que una reanudación libere días.
  const extensionBudget = new Map();
  let lastExtensionId = 0;
  // Días suspendidos que una prórroga ya liquidó al calcular el saldo: su
  // dinero se movió al nuevo tramo, así que una reanudación posterior no
  // puede devolverlos al plan (se pagarían dos veces).
  const settledDays = adjustedPaymentPlan.map(() => new Set());

  const ensureMonth = (idx) => {
    while (planDays.length <= idx) {
      adjustedPaymentPlan.push(0);
      planDays.push(new Map());
      suspendedDays.push(new Set());
      dayExtension.push(new Map());
      settledDays.push(new Set());
    }
  };

  // Valor de un mes: la suma de las tarifas de sus días no suspendidos.
  const monthValue = (idx, includeDay) => {
    let total = 0;
    planDays[idx].forEach((dailyPayment, day) => {
      if (!suspendedDays[idx].has(day) && (!includeDay || includeDay(day))) total += dailyPayment;
    });
    return total;
  };
  const refreshMonth = (idx) => { adjustedPaymentPlan[idx] = monthValue(idx); };

  // Las suspensiones se consultan al reprogramar una prórroga, pero solo
  // las YA aplicadas: la pila es secuencial y el orden en que se registran
  // las novedades es parte de la información (invertir dos novedades es un
  // escenario contractual distinto y debe dar un resultado distinto).
  const appliedSuspensions = [];

  // Las novedades se aplican en orden CRONOLÓGICO, no en el de captura: una
  // suspensión que empezó antes ya había afectado el saldo cuando se pactó
  // la prórroga, aunque se haya registrado después. El orden de registro
  // solo desempata novedades que arrancan el mismo día (ahí sí decide cuál
  // se aplica primero, y el resultado es distinto según el caso).
  const chronologicalNews = news
    .map((novelty, order) => ({ novelty, order }))
    .sort((a, b) => {
      if (!a.novelty.startedAt || !b.novelty.startedAt) return a.order - b.order;
      if (a.novelty.startedAt.isSame(b.novelty.startedAt, 'day')) return a.order - b.order;
      return a.novelty.startedAt.isBefore(b.novelty.startedAt) ? -1 : 1;
    })
    .map(({ novelty }) => novelty);

  chronologicalNews.forEach(n => {
    if (!n.startedAt) return;

    switch (n.type) {
      case 'ADDITION_AND_EXTENSION': {
        // `amount` llega como cadena vacía desde el formulario cuando la
        // novedad es solo prórroga en tiempo (caso típico de una licencia),
        // así que la extensión se detecta por la fecha de fin, no por el
        // monto.
        const amount = Number(n.amount) || 0;
        const hasExtension = !!(n.finishedAt && planFinishedAt && moment(n.finishedAt).isAfter(planFinishedAt, 'day'));

        if (!hasExtension) {
          if (amount && n.finishedAt) {
            // Adición sin prórroga: el monto se reparte por días entre los
            // meses que cubre la novedad, subiendo la tarifa de esos días.
            const startIndex = Math.max(0, monthIndexAt(n.startedAt));
            const endIndex = monthIndexAt(n.finishedAt);
            const daysByMonth = {};
            let totalDays = 0;

            for (let i = startIndex; i <= endIndex; i++) {
              daysByMonth[i] = monthDaysInRange(monthMomentAt(i), n.startedAt, n.finishedAt);
              totalDays += daysByMonth[i].size;
            }

            if (totalDays > 0) {
              const dailyAmount = amount / totalDays;

              for (let i = startIndex; i <= endIndex; i++) {
                ensureMonth(i);
                daysByMonth[i].forEach(day => {
                  planDays[i].set(day, (planDays[i].get(day) || 0) + dailyAmount);
                });
                refreshMonth(i);
              }
            }
          }
        } else {
          // Prórroga: el plan vigente se cierra el día anterior al inicio
          // de la novedad y el saldo no ejecutado (más la adición, si la
          // hay) se reparte sobre el nuevo plazo.
          const cutoffDate = moment(n.startedAt).subtract(1, 'day');
          const cutoffIndex = monthIndexAt(cutoffDate);
          const cutoffDay = Math.min(30, cutoffDate.date());
          const isExecutedDay = (day) => day <= cutoffDay;

          let paidAmount = 0;
          for (let i = 0; i <= cutoffIndex && i < planDays.length; i++) {
            paidAmount += monthValue(i, i === cutoffIndex ? isExecutedDay : undefined);
          }

          // Lo suspendido hasta el corte ya quedó descontado del saldo que
          // se acaba de repartir: se marca como liquidado.
          for (let i = 0; i <= cutoffIndex && i < planDays.length; i++) {
            suspendedDays[i].forEach(day => {
              if (i < cutoffIndex || isExecutedDay(day)) settledDays[i].add(day);
            });
          }

          // El mes de corte conserva solo lo ejecutado y los meses
          // posteriores se liberan: el nuevo plazo los reprograma.
          for (let i = Math.max(0, cutoffIndex); i < planDays.length; i++) {
            const keepDay = (day) => i === cutoffIndex && isExecutedDay(day);

            [...planDays[i].keys()].forEach(day => {
              if (keepDay(day)) return;
              // El saldo pendiente del tramo liberado ya vuelve a contarse
              // en el saldo global que reparte esta prórroga.
              const releasedExtension = dayExtension[i].get(day);
              if (releasedExtension !== undefined) extensionBudget.delete(releasedExtension);
              planDays[i].delete(day);
            });
            [...suspendedDays[i]].forEach(day => { if (!keepDay(day)) suspendedDays[i].delete(day); });
            [...dayExtension[i].keys()].forEach(day => { if (!keepDay(day)) dayExtension[i].delete(day); });
            [...settledDays[i]].forEach(day => { if (!keepDay(day)) settledDays[i].delete(day); });
            refreshMonth(i);
          }

          const totalBudgetForExtension = (planContractBudget - paidAmount) + amount;
          const startIndex = Math.max(0, monthIndexAt(n.startedAt));
          const endIndex = monthIndexAt(n.finishedAt);
          const extensionByMonth = {};
          let totalExtensionDays = 0;

          for (let i = startIndex; i <= endIndex; i++) {
            const monthMoment = monthMomentAt(i);
            const days = monthDaysInRange(monthMoment, n.startedAt, n.finishedAt);

            // Los días que ya estaban suspendidos cuando se pactó la
            // prórroga no se pagan: el saldo se reparte solo entre los días
            // ejecutables del nuevo plazo, en vez de perderse.
            const suspended = new Set();
            appliedSuspensions.forEach(s => {
              monthDaysInRange(monthMoment, s.startedAt, s.finishedAt).forEach(day => {
                if (days.has(day)) suspended.add(day);
              });
            });

            extensionByMonth[i] = { days, suspended };
            totalExtensionDays += days.size - suspended.size;
          }

          const dailyPayment = totalExtensionDays > 0 ? totalBudgetForExtension / totalExtensionDays : 0;
          const extensionId = ++lastExtensionId;
          extensionBudget.set(extensionId, totalBudgetForExtension);

          for (let i = startIndex; i <= endIndex; i++) {
            ensureMonth(i);
            const { days, suspended } = extensionByMonth[i];

            days.forEach(day => {
              // Un día ya suspendido entra al tramo con tarifa cero: su
              // parte del saldo se repartió entre los días ejecutables, así
              // que una reanudación posterior lo devuelve al plan sin
              // dinero en vez de duplicar lo ya reprogramado.
              planDays[i].set(day, suspended.has(day) ? 0 : dailyPayment);
              dayExtension[i].set(day, extensionId);
              if (suspended.has(day)) suspendedDays[i].add(day);
            });
            refreshMonth(i);
          }
        }

        // Acumular el efecto de esta novedad para las siguientes
        if (amount) planContractBudget += amount;
        if (n.finishedAt && (!planFinishedAt || n.finishedAt.isAfter(planFinishedAt, 'day'))) {
          planFinishedAt = moment(n.finishedAt);
        }
        break;
      }

      case 'SUSPENSION': {
        if (!n.finishedAt) break;

        const startIndex = Math.max(0, monthIndexAt(n.startedAt));
        const endIndex = monthIndexAt(n.finishedAt);
        // Días suspendidos que pertenecen a un tramo prorrogado, por tramo.
        const suspendedByExtension = new Map();

        for (let i = startIndex; i <= endIndex && i < planDays.length; i++) {
          monthDaysInRange(monthMomentAt(i), n.startedAt, n.finishedAt).forEach(day => {
            if (!planDays[i].has(day) || suspendedDays[i].has(day)) return;

            suspendedDays[i].add(day);
            const extension = dayExtension[i].get(day);
            if (extension !== undefined) {
              if (!suspendedByExtension.has(extension)) suspendedByExtension.set(extension, []);
              suspendedByExtension.get(extension).push([i, day]);
            }
          });
          refreshMonth(i);
        }

        // Lo suspendido dentro de una prórroga se reparte entre los días
        // que quedan de ESE tramo después de la suspensión: el plazo ya
        // llega hasta el final del tramo, así que el saldo se corre en vez
        // de perderse. Si no quedan días, queda como saldo sin ejecutar.
        if (suspendedByExtension.size) {
          const endDay = Math.min(30, moment(n.finishedAt).date());
          const isAfterSuspension = (i, day) => i > endIndex || (i === endIndex && day > endDay);

          suspendedByExtension.forEach((sourceDays, extension) => {
            const targets = [];

            for (let i = Math.max(0, endIndex); i < planDays.length; i++) {
              dayExtension[i].forEach((id, day) => {
                if (id === extension && !suspendedDays[i].has(day) && isAfterSuspension(i, day)) targets.push([i, day]);
              });
            }
            if (!targets.length) return;

            const displacedAmount = sourceDays.reduce((total, [i, day]) => total + planDays[i].get(day), 0);
            const perDay = displacedAmount / targets.length;
            const touched = new Set();

            // El día de origen queda en cero: su valor se movió. Si más
            // adelante una reanudación lo devuelve al plan, entra sin
            // dinero en vez de duplicar lo que ya se reprogramó.
            sourceDays.forEach(([i, day]) => { planDays[i].set(day, 0); touched.add(i); });
            targets.forEach(([i, day]) => {
              planDays[i].set(day, planDays[i].get(day) + perDay);
              touched.add(i);
            });
            touched.forEach(refreshMonth);
          });
        }

        appliedSuspensions.push(n);
        break;
      }

      case 'TERMINATION': {
        const terminationIndex = monthIndexAt(n.startedAt);
        if (terminationIndex < 0 || terminationIndex >= planDays.length) break;

        // El contrato se ejecuta hasta el día anterior a la terminación.
        const lastDay = Math.min(30, moment(n.startedAt).date()) - 1;

        [...planDays[terminationIndex].keys()].forEach(day => {
          if (day > lastDay) {
            planDays[terminationIndex].delete(day);
            suspendedDays[terminationIndex].delete(day);
            dayExtension[terminationIndex].delete(day);
            settledDays[terminationIndex].delete(day);
          }
        });
        refreshMonth(terminationIndex);

        const keepLength = planDays[terminationIndex].size === 0 ? terminationIndex : terminationIndex + 1;
        adjustedPaymentPlan = adjustedPaymentPlan.slice(0, keepLength);
        planDays.length = keepLength;
        suspendedDays.length = keepLength;
        dayExtension.length = keepLength;
        settledDays.length = keepLength;
        break;
      }

      case 'SUSPENSION_RESUMPTION': {
        if (!n.finishedAt) break;

        // Liquida el saldo a favor que dejaron las suspensiones: abre un tramo
        // después del fin del contrato y paga cada uno de sus días a la tarifa
        // vigente. Lo que el rango no alcance a consumir queda disponible para
        // otra reanudación.
        const resumptionRate = lastDailyRate(planDays);
        if (!resumptionRate) break;

        let availableBalance = planContractBudget - adjustedPaymentPlan.reduce((total, value) => total + value, 0);
        if (availableBalance <= 0) break;

        const startIndex = Math.max(0, monthIndexAt(n.startedAt));
        const endIndex = monthIndexAt(n.finishedAt);
        const resumptionId = ++lastExtensionId;

        for (let i = startIndex; i <= endIndex; i++) {
          ensureMonth(i);

          const resumptionDays = [...monthDaysInRange(monthMomentAt(i), n.startedAt, n.finishedAt)].sort((x, y) => x - y);
          for (const day of resumptionDays) {
            // El saldo acota el reparto aunque el formulario ya valide el
            // tope: el plan nunca puede superar el presupuesto del contrato.
            const dayPayment = Math.min(resumptionRate, Math.max(0, availableBalance));
            availableBalance -= dayPayment;
            planDays[i].set(day, dayPayment);
            // El tramo se marca como los de prórroga, para que una suspensión
            // posterior sobre él corra el dinero en vez de perderlo.
            dayExtension[i].set(day, resumptionId);
          }

          refreshMonth(i);
        }

        if (!planFinishedAt || moment(n.finishedAt).isAfter(planFinishedAt, 'day')) {
          planFinishedAt = moment(n.finishedAt);
        }
        break;
      }

      case 'RESUMPTION_AFTER_SUSPENSION': {
        const activeSuspension = news.find(s => (
          s.type === 'SUSPENSION'
          && s.startedAt
          && s.finishedAt
          && n.startedAt.isSameOrAfter(s.startedAt)
          && n.startedAt.isSameOrBefore(s.finishedAt)
        ));
        if (!activeSuspension) break;

        const startIndex = Math.max(0, monthIndexAt(n.startedAt));
        const endIndex = monthIndexAt(activeSuspension.finishedAt);
        const startDay = Math.min(30, moment(n.startedAt).date());
        const resumedExtensions = new Set();

        for (let i = startIndex; i <= endIndex && i < planDays.length; i++) {
          monthDaysInRange(monthMomentAt(i), n.startedAt, activeSuspension.finishedAt).forEach(day => {
            if (settledDays[i].has(day) || !suspendedDays[i].delete(day)) return;

            const extension = dayExtension[i].get(day);
            if (extension !== undefined) resumedExtensions.add(extension);
          });
          refreshMonth(i);
        }

        // Saldo del tramo que quedó sin colocar (la prórroga se pactó sobre
        // días suspendidos): se reparte entre los días del tramo que quedan
        // ejecutables desde la reanudación en adelante.
        resumedExtensions.forEach(extension => {
          const budget = extensionBudget.get(extension);
          if (budget === undefined) return;

          const targets = [];
          let placedAmount = 0;

          for (let i = 0; i < planDays.length; i++) {
            for (const [day, id] of dayExtension[i]) {
              if (id !== extension) continue;

              placedAmount += planDays[i].get(day);
              const isFromResumption = i > startIndex || (i === startIndex && day >= startDay);
              if (!suspendedDays[i].has(day) && isFromResumption) targets.push([i, day]);
            }
          }

          const pendingAmount = budget - placedAmount;
          if (pendingAmount <= 0 || !targets.length) return;

          const perDay = pendingAmount / targets.length;
          const touched = new Set();
          targets.forEach(([i, day]) => {
            planDays[i].set(day, planDays[i].get(day) + perDay);
            touched.add(i);
          });
          touched.forEach(refreshMonth);
        });
        break;
      }

      default:
        break;
    }
  });

  return { paymentPlan: adjustedPaymentPlan, planDays, suspendedDays, planContractBudget, planFinishedAt };
};

export const getContractField = (news, contract, limitDate) => ((key) => {
  if (!contract || !news || !Array.isArray(news)) return contract?.[key];
  
  const now = moment();
  const {
    status,
    finishedAt,
    contractPeriod,
    contractBudget,
    supervisors
  } = contract;

  const isBeforeApproval = [CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status);

  const newsToProcess = limitDate !== undefined
    ? news.filter(n => {
        if (!n.startedAt) return false;
        const limitMoment = moment(limitDate);
        return limitMoment.isSameOrAfter(n.startedAt);
      })
    : news;

  // Días EJECUTABLES de cada periodo: arreglo paralelo a `paymentPlan` que por
  // cada índice lleva los días del mes (convención comercial: 1 a 30) a los
  // que el plan vigente asigna tarifa y que no quedaron suspendidos. De aquí
  // sale el rango de fechas real del periodo, que no es el mes calendario
  // cuando una suspensión lo recorta.
  //
  // Se resuelve ANTES del corto circuito de DRAFT/SUPERVISOR porque no existe
  // un campo almacenado equivalente que devolver: sin esto el rango quedaría
  // sin resolver justo en los estados en que el plan se edita. En esos estados
  // las novedades no aplican, así que el calendario se arma sin ellas.
  //
  // El filtro es SOLO por suspensión y no por tarifa mayor que cero: los días
  // cuyo valor se movió a otro tramo al pactar una prórroga quedan en cero
  // pero también quedan en `suspendedDays`, así que ya salen por ahí. Filtrar
  // además por valor borraría días legítimamente ejecutados.
  if (key === 'paymentPlanDays') {
    const daysCalendar = buildPaymentCalendar(contract, isBeforeApproval ? [] : newsToProcess);
    if (!daysCalendar) return null;

    return daysCalendar.planDays.map((days, i) => [...days.keys()]
      .filter((day) => !daysCalendar.suspendedDays[i].has(day))
      .sort((a, b) => a - b));
  }

  if (isBeforeApproval) return contract[key];

  switch (key) {
    case 'finishedAt':
      let tmpFinishedAt = finishedAt;
      newsToProcess.forEach(n => {
        if (['ADDITION_AND_EXTENSION', 'SUSPENSION_RESUMPTION'].includes(n.type)
          && n.finishedAt
          && n.finishedAt.isAfter(tmpFinishedAt))
        {
          tmpFinishedAt = n.finishedAt;
        }

        if (n.type === 'TERMINATION'
          && n.startedAt
          && n.startedAt.isBefore(tmpFinishedAt))
        {
            tmpFinishedAt = moment(n.startedAt).subtract(1, 'days');
        }
      });

      return tmpFinishedAt;

    case 'contractPeriod':
      let tmpContractPeriod = contractPeriod;
      let currentFinishedAt = finishedAt;

      newsToProcess.forEach(n => {
        if (n.type === 'ADDITION_AND_EXTENSION'
          && n.finishedAt
          && currentFinishedAt
          && n.finishedAt.isAfter(currentFinishedAt, 'day'))
        {
          // Días de la prórroga en convención comercial (meses de 30 días),
          // igual que el resto de cálculos del contrato: desde el día
          // siguiente al fin vigente hasta el nuevo fin, ambos inclusive.
          // Se mide contra el fin vigente acumulado (no contra n.startedAt)
          // para que adiciones sin prórroga o con fechas solapadas no
          // alteren la duración.
          const start = moment(currentFinishedAt).add(1, 'day');
          const end = n.finishedAt;
          const months = (end.year() - start.year()) * 12 + (end.month() - start.month());

          let extensionDays;
          if (months === 0) {
            extensionDays = Math.max(0, Math.min(30, end.date()) - start.date() + 1);
          } else {
            const firstMonthDays = Math.max(0, 30 - start.date() + 1);
            const lastMonthDays = Math.min(30, end.date());
            extensionDays = firstMonthDays + ((months - 1) * 30) + lastMonthDays;
          }

          tmpContractPeriod = Number(tmpContractPeriod) + extensionDays;
          currentFinishedAt = n.finishedAt;
        }

        // La reanudación puede empezar días después del fin del contrato, así
        // que su aporte al plazo son los días de su propio rango y no los que
        // van desde el fin vigente.
        if (n.type === 'SUSPENSION_RESUMPTION' && n.startedAt && n.finishedAt) {
          tmpContractPeriod = Number(tmpContractPeriod) + countContractDays(n.startedAt, n.finishedAt);
          currentFinishedAt = n.finishedAt;
        }
      });

      return tmpContractPeriod;

    case 'contractBudget':
      let tmpContractBudget = contractBudget;
      newsToProcess.forEach(n => {
        if (n.type === 'ADDITION_AND_EXTENSION' && n.amount) {
          tmpContractBudget += n.amount;
        }
      });

      return tmpContractBudget;

    case 'paymentPlan':
      return buildPaymentCalendar(contract, newsToProcess)?.paymentPlan ?? null;


    case 'dailyRate': {
      // Tarifa del último día con valor del calendario vigente: la del último
      // tramo pactado, que en un contrato prorrogado no coincide con salary/30.
      const rateCalendar = buildPaymentCalendar(contract, newsToProcess);
      return rateCalendar ? lastDailyRate(rateCalendar.planDays) : 0;
    }

    case 'balance': {
      // Presupuesto a favor: lo que el contrato tiene aprobado menos lo que el
      // plan vigente alcanza a pagar. Lo alimentan los días suspendidos y, si
      // la hay, la terminación anticipada.
      const balanceCalendar = buildPaymentCalendar(contract, newsToProcess);
      if (!balanceCalendar) return 0;

      const planned = balanceCalendar.paymentPlan.reduce((total, value) => total + value, 0);
      return Math.max(0, balanceCalendar.planContractBudget - planned);
    }

    case 'status':
      if ([CONTRACT_STATUS.CLOSED].includes(status)) return status;

      const effectiveFinishedAt = getContractField(newsToProcess, contract, limitDate)('finishedAt');
      const hasTerminated = newsToProcess.some(n => 
        n.type === 'TERMINATION' &&
        n.startedAt &&
        now.isSameOrAfter(n.startedAt)
      );
      if (hasTerminated) return CONTRACT_STATUS.FINISHED;

      const hasActiveSuspension = newsToProcess.some(n => {
        if (n.type !== 'SUSPENSION' || !n.startedAt || !n.finishedAt) return false;
        if (!now.isSameOrAfter(n.startedAt) || !now.isSameOrBefore(n.finishedAt)) return false;

        const hasResumption = newsToProcess.some(r =>
          r.type === 'RESUMPTION_AFTER_SUSPENSION'
          && r.startedAt
          && r.startedAt.isSameOrAfter(n.startedAt)
          && r.startedAt.isSameOrBefore(n.finishedAt)
          && now.isSameOrAfter(r.startedAt)
        );

        return !hasResumption;
      });
      if (hasActiveSuspension) return CONTRACT_STATUS.SUSPENDED;

      if (effectiveFinishedAt && moment(effectiveFinishedAt).isBefore(now)) {
        return CONTRACT_STATUS.FINISHED;
      }

      return status;

    case 'supervisors':
      // Evaluar la vigencia contra la fecha de la cuenta de cobro (limitDate),
      // no contra hoy: una asignación temporal ya vencida debe seguir aplicando
      // al regenerar la cuenta de un periodo en que estuvo activa.
      // Mantener sincronizado con getEffectiveSupervisors en
      // gfunctions/handleContract/index.js y gfunctions/handleContractLegal/index.js.
      const supervisorRefDate = limitDate !== undefined ? moment(limitDate) : now;
      let tSupervisors = [...supervisors];
      newsToProcess.forEach((n) => {
        if (
          (n.type === "CHANGE_SUPERVISOR"
            && n.startedAt && supervisorRefDate.isSameOrAfter(n.startedAt)
          ) || (
            n.type === 'ASSIGN_SUPERVISOR'
            && n.startedAt && supervisorRefDate.isSameOrAfter(n.startedAt)
            && n.finishedAt && supervisorRefDate.isSameOrBefore(n.finishedAt)
          )
        ) {
          tSupervisors = n.supervisor ? [n.supervisor] : supervisors;
        }
      });

      return tSupervisors;
    
    case 'tasks':
      const tmpTasks = [...contract.tasks];

       newsToProcess.forEach((n) => {
        if (n.type === 'RESPONSIBILITIES_CHANGE') {
          n.tasks.forEach((task) => {
            switch (task.action) {
              case 'ADD':
                tmpTasks.push(task);
                break;
              case 'DELETE':
                const idxTask = tmpTasks.findIndex(t => t.title === task.title);
                if (idxTask !== -1) {
                  tmpTasks.splice(idxTask, 1);
                }
                break;
              default:
                break;
            }
          });
        }
      });

      return tmpTasks;
    default:
      return newsToProcess;
  }
});

// Progreso de revisión de los apoyos asignados sobre un componente. El estado
// `Revisada` NO se persiste: se deriva del mapa `reviews` contra los
// `supportIDs` vigentes del contrato (los apoyos asignados pueden cambiar).
// Basta con que AL MENOS UN apoyo marque su revisión; no se exige que todos
// lo hagan. Las revisiones de apoyos ya no asignados se conservan pero no cuentan.
export const getSupportReviewProgress = (reviews, supportIDs) => {
  const ids = supportIDs || [];
  const reviewedIDs = ids.filter((id) => reviews?.[id]);
  return {
    total: ids.length,
    count: reviewedIDs.length,
    complete: ids.length > 0 && reviewedIDs.length === ids.length,
    reviewedIDs,
    pendingIDs: ids.filter((id) => !reviews?.[id]),
  };
};

// Estado de revisión de una actividad. Único punto de lectura del esquema:
// tolera datos anteriores a devolucion-flow, donde la devolución se marcaba
// con el flag tri-estado `rejected` (true = devuelta, false = corregida).
// La actividad no tiene estado Revisada propio: la revisión de los apoyos es
// una marca única por mes sobre el componente de Actividades
// (payments.payment_N.activitiesReview), no por actividad.
export const getActivityStatus = (activity) => {
  if (!activity) return REVIEW_STATUS.PENDING;
  return activity.reviewStatus
    ? activity.reviewStatus
    : activity.rejected === true ? REVIEW_STATUS.RETURNED : REVIEW_STATUS.PENDING;
};

// Estado agregado del componente de Actividades de un mes: el peor estado
// entre sus actividades, con precedencia Devuelta > Pendiente > Revisada >
// Aprobada. `Revisada` se deriva del bloque (al menos un apoyo marcó en
// activitiesReview.reviews) y `Aprobada` de la aprobación del acta por el
// Supervisor (payment.approved), ya que ninguna se persiste por actividad.
export const getActivitiesAggregateStatus = (payment, supportIDs) => {
  const statuses = (payment?.activities || []).map(getActivityStatus);
  if (statuses.length === 0) return REVIEW_STATUS.PENDING;
  if (statuses.includes(REVIEW_STATUS.RETURNED)) return REVIEW_STATUS.RETURNED;
  if (payment?.approved === true) return REVIEW_STATUS.APPROVED;
  if (statuses.includes(REVIEW_STATUS.PENDING)) {
    // La marca de revisión puede venir de un Apoyo asignado o del Supervisor:
    // cualquier entrada en reviews{} deriva el componente como Revisada.
    return hasActivitiesReviewMark(payment)
      ? REVIEW_STATUS.REVIEWED
      : REVIEW_STATUS.PENDING;
  }
  if (statuses.includes(REVIEW_STATUS.REVIEWED)) return REVIEW_STATUS.REVIEWED;
  return REVIEW_STATUS.APPROVED;
};

// ¿Alguien (Apoyo o Supervisor) marcó como revisadas las actividades del mes?
// Prerrequisito para habilitar la aprobación de la cuenta de cobro.
export const hasActivitiesReviewMark = (payment) => (
  Object.keys(payment?.activitiesReview?.reviews || {}).length > 0
);

// Estado de revisión de la seguridad social de un mes de pago. Único punto de
// lectura del esquema. Estados posibles: Pendiente, Devuelta y Revisada, que
// es el estado TERMINAL del componente (no existe Aprobada). `Revisada` se
// deriva de cualquier marca en socialSecurityReview.reviews (Supervisor o
// Apoyo, una basta; por eso no se filtra por supportIDs) y, por compatibilidad
// en lectura, de los registros legados aprobados (socialApproved / status
// 'approved'); esos campos ya no se escriben y no se migran.
export const getSocialSecurityStatus = (payment) => {
  const status = payment?.socialSecurityReview?.status;
  if (status === REVIEW_STATUS.RETURNED) return REVIEW_STATUS.RETURNED;
  if (
    status === REVIEW_STATUS.APPROVED
    || payment?.socialApproved === true
    || Object.keys(payment?.socialSecurityReview?.reviews || {}).length > 0
  ) {
    return REVIEW_STATUS.REVIEWED;
  }
  return status || REVIEW_STATUS.PENDING;
};

// Estado de PRESENTACIÓN de un componente: si está PENDING pero ya
// diligenciado/enviado, se muestra como 'submitted' (amarillo); en cualquier
// otro caso, el estado real. No altera el estado almacenado.
export const getReviewDisplayStatus = (status, filled) => (
  status === REVIEW_STATUS.PENDING && filled ? REVIEW_DISPLAY_SUBMITTED : status
);

// ¿El mes está "En corrección"? Ciclo iniciado por la notificación agrupada de
// devoluciones y cerrado por la notificación de corrección del contratista.
export const isMonthInCorrection = (payment) => (
  payment?.activitiesReview?.correction?.status === CORRECTION_STATUS.IN_CORRECTION
);

// ¿La actividad fue agregada por el contratista en el ciclo de corrección
// vigente (o último) del mes? Se deriva de `addedAt` contra el inicio del
// ciclo (`correction.startedAt`, ambos ISO UTC: comparación lexicográfica).
// Las actividades sin `addedAt` son originales del mes (sin migración).
export const isActivityAddedInCycle = (activity, payment) => {
  const startedAt = payment?.activitiesReview?.correction?.startedAt;
  return !!(activity?.addedAt && startedAt && activity.addedAt >= startedAt);
};

// ¿Hay actividades agregadas en el ciclo vigente sin descripción? Bloquea la
// notificación de corrección del contratista hasta completarlas.
export const hasAddedActivitiesWithoutDescription = (activities = [], payment) => (
  activities.some((act) => (
    isActivityAddedInCycle(act, payment) && !(act.description && act.description.trim())
  ))
);

// Props de color para un icono MUI según el estado de presentación: usa la
// paleta del theme salvo el amarillo 'submitted' (por sx, MUI no tiene paleta
// amarilla). 'default' se mapea a 'action' (gris) por no ser color de icono.
export const getReviewIconColorProps = (displayStatus) => {
  if (displayStatus === REVIEW_DISPLAY_SUBMITTED) {
    return { sx: { color: REVIEW_SUBMITTED_COLOR } };
  }
  const ui = REVIEW_STATUS_UI[displayStatus];
  return { color: ui?.color && ui.color !== 'default' ? ui.color : 'action' };
};

// Seguridad social "diligenciada" de un mes (el estado amarillo de la tabla de
// SS): datos completos + planilla cargada. Solo la planilla (`certificates.form`)
// cuenta como documento obligatorio.
export const isSocialSecurityFilled = (payment) => !!(
  payment
  && payment.contribution
  && payment.payroll
  && payment.payAt
  && payment?.certificates?.form
);

// Estado de PRESENTACIÓN de la seguridad social de un mes, independiente del
// ciclo de la cuenta de cobro (el envío del mes no cambia el color del
// componente): Rojo (Devuelta) > Verde (Revisada, terminal) > Amarillo
// (diligenciada, pendiente de revisión) > Gris (Pendiente sin diligenciar).
export const getSocialSecurityDisplayStatus = (payment) => {
  const status = getSocialSecurityStatus(payment);
  if (status === REVIEW_STATUS.RETURNED) return REVIEW_STATUS.RETURNED;
  if (status === REVIEW_STATUS.REVIEWED) return REVIEW_DISPLAY_SS_REVIEWED;
  return isSocialSecurityFilled(payment) ? REVIEW_DISPLAY_SUBMITTED : REVIEW_STATUS.PENDING;
};

// Devolución vigente de la seguridad social de un mes: la última entrada de
// socialSecurityReview.returns no deshecha.
export const getActiveSSReturn = (payment) => (
  (payment?.socialSecurityReview?.returns || []).filter((ret) => !ret.undone).slice(-1)[0] || null
);

// Campos marcados en la devolución vigente de la SS que SIGUEN IGUALES a su
// snapshot en `candidate` (los valores a punto de guardarse o los del
// formulario). Devuelve [] cuando la corrección está completa y null cuando no
// hay devolución vigente o no tiene snapshot (devolución legada: conserva el
// comportamiento anterior de reset con cualquier edición). Normalización:
// contribution como Number, payAt a YYYY-MM-DD y certificates corregido si
// alguna URL difiere del snapshot.
export const getSSPendingCorrectionFields = (payment, candidate) => {
  const activeReturn = getActiveSSReturn(payment);
  if (!activeReturn || !activeReturn.snapshot) return null;

  const { snapshot } = activeReturn;
  const normalizeDate = (value) => (value ? moment(value).format('YYYY-MM-DD') : null);

  return (activeReturn.fields || []).filter((field) => {
    if (field === 'payAt') {
      return normalizeDate(candidate?.payAt) === normalizeDate(snapshot.payAt);
    }
    if (field === 'contribution') {
      return Number(candidate?.contribution || 0) === Number(snapshot.contribution || 0);
    }
    if (field === 'certificates') {
      return ['form', 'payroll', 'transaction', 'pensioner'].every((key) => (
        (candidate?.certificates?.[key] || null) === (snapshot.certificates?.[key] || null)
      ));
    }
    return (candidate?.[field] || '') === (snapshot[field] || '');
  });
};

// ---- Periodos ejecutables del plan de pagos ------------------------------
// Una suspensión puede dejar meses del plan vigente en cero (sin un solo día
// ejecutable). Esos índices siguen existiendo porque `payments.payment_N` ES
// el índice del plan, pero no son periodos de pago: no generan cuenta de
// cobro, no exigen seguridad social y no consumen número. Estos tres helpers
// son la única definición de "periodo ejecutable", "periodo anterior" y
// "número de periodo"; todo consumidor debe pasar por aquí y no por
// `idx - 1` / `idx + 1`.

// Un índice es ejecutable cuando el plan vigente le asigna valor.
export const isExecutablePeriod = (paymentPlan, idx) => (
  Array.isArray(paymentPlan) && Number(paymentPlan[idx]) > 0
);

// Índice del periodo ejecutable inmediatamente anterior a `idx`, saltando los
// meses en cero. -1 cuando no hay ninguno: ese `idx` es el primer periodo y
// se le aplican las reglas del Mes 1 (certificados de afiliación en lugar de
// seguridad social previa).
export const getPreviousExecutableIdx = (paymentPlan, idx) => {
  if (!Array.isArray(paymentPlan) || !Number.isInteger(idx)) return -1;
  for (let i = idx - 1; i >= 0; i--) {
    if (isExecutablePeriod(paymentPlan, i)) return i;
  }
  return -1;
};

// Número consecutivo del periodo ("Mes N", número de la cuenta de cobro):
// cuántos índices ejecutables hay en [0, idx]. Un mes en cero no tiene número
// (null). Sin meses en cero coincide con `idx + 1`.
export const getPeriodNumber = (paymentPlan, idx) => {
  if (!isExecutablePeriod(paymentPlan, idx)) return null;
  let number = 0;
  for (let i = 0; i <= idx; i++) {
    if (isExecutablePeriod(paymentPlan, i)) number++;
  }
  return number;
};

// Índice del periodo ejecutable inmediatamente posterior a `idx`, saltando
// los meses en cero. -1 cuando `idx` es el último periodo del plan.
export const getNextExecutableIdx = (paymentPlan, idx) => {
  if (!Array.isArray(paymentPlan) || !Number.isInteger(idx)) return -1;
  for (let i = idx + 1; i < paymentPlan.length; i++) {
    if (isExecutablePeriod(paymentPlan, i)) return i;
  }
  return -1;
};

// Índice de la ÚLTIMA cuenta de cobro aprobada del plan (-1 si ninguna). Es
// la única que admite reversa de aprobación: las anteriores se revierten en
// secuencia, de la más reciente hacia atrás, porque cada periodo encadena la
// aprobación del anterior.
export const getLastApprovedPaymentIdx = (paymentPlan, payments) => {
  if (!Array.isArray(paymentPlan)) return -1;
  for (let i = paymentPlan.length - 1; i >= 0; i--) {
    if (isExecutablePeriod(paymentPlan, i) && payments?.[`payment_${i}`]?.approved === true) return i;
  }
  return -1;
};

// Campos que escribe la aprobación del acta (ContractView) y la generación de
// sus PDFs (ContractView / ContractViewActions). La reversa los borra todos.
const APPROVAL_FIELDS = [
  'approved', 'approvedAt', 'approvedEmpty', 'approvedEmptyBy',
  'chargeAccount', 'chargeAccountHash', 'hashFileUrl', 'chargeAccountFull',
];

// Pago resultante de revertir la aprobación de su cuenta de cobro. Deshace
// exactamente lo que hizo la aprobación:
// - borra la aprobación y sus metadatos (fecha, PDF, hash, consolidado);
// - retorna a Pendiente las actividades que la aprobación marcó Aprobadas,
//   para que el Supervisor pueda volver a devolverlas;
// - deja el acta enviada (`approved: false`), es decir, en revisión del
//   Supervisor. Una aprobación vacía sin envío previo vuelve a borrador.
// Conserva las marcas de revisión, la seguridad social y el historial de
// devoluciones, que no dependen de la aprobación. Cada reversa queda en
// `approvalReversals` con su autor, motivo y el hash del PDF que se anuló.
export const buildRevertedApprovalPayment = (payment, actor, reason, at = new Date().toISOString()) => {
  const reverted = { ...(payment || {}) };
  APPROVAL_FIELDS.forEach((field) => delete reverted[field]);

  if (Array.isArray(reverted.activities)) {
    reverted.activities = reverted.activities.map((act) => (
      getActivityStatus(act) === REVIEW_STATUS.APPROVED
        ? { ...act, reviewStatus: REVIEW_STATUS.PENDING }
        : act
    ));
  }

  const backToDraft = payment?.approvedEmpty && !payment?.sentAt && !payment?.activities?.length;
  if (!backToDraft) reverted.approved = false;

  reverted.approvalReversals = [
    ...(payment?.approvalReversals || []),
    {
      userId: actor.userId,
      name: actor.name,
      at,
      reason,
      approvedAt: payment?.approvedAt ?? null,
      approvedEmpty: !!payment?.approvedEmpty,
      chargeAccountHash: payment?.chargeAccountHash ?? null,
    },
  ];

  return reverted;
};

// Fechas de un periodo del plan, derivadas de sus días EJECUTABLES
// (`paymentPlanDays`) y no del mes calendario: una suspensión que arranca el
// 20 de abril deja el periodo de abril en 1-19, y el mes en que termina
// arranca el día siguiente al último día suspendido.
//
// FUENTE ÚNICA del rango: la fila del plan de pagos, el modal del plan, el
// modal y la tabla de seguridad social, el encabezado de la cuenta de cobro,
// el periodo impreso en el PDF y el `rangeDate` de los correos pasan por aquí.
// Ninguna vista debe reconstruir el rango a partir del mes.
//
// Devuelve la ENVOLVENTE (primer y último día ejecutable). Cuando una
// suspensión parte el mes en dos tramos, `isContiguous` es false y `gaps`
// lleva los tramos suspendidos que quedan dentro de la envolvente, para que
// la vista lo señale: la etiqueta sola no puede expresarlo.
//
// `null` cuando el periodo no tiene días ejecutables (mes en cero): no es un
// periodo de pago y ninguna superficie lo rotula.
// Valor de una tabla anual (SALARIO_MINIMO, UVT) para `year`. Si el año aún no
// está cargado se usa el último anterior que lo esté (y, antes del primero, el
// primero): un año sin valor dejaba los descuentos en NaN.
const getYearValue = (table, year) => {
  const years = Object.keys(table).map(Number).sort((a, b) => a - b);
  const known = years.filter((y) => y <= year);
  return table[known.length ? known[known.length - 1] : years[0]];
};

// Salario mínimo y UVT vigentes en el periodo `idx` del plan mensual. Se
// resuelven por el año del PERIODO y no por el de inicio del contrato: un
// contrato que cruza de año liquida cada mes con los valores de su año.
export const getPeriodTaxValues = (startedAt, idx) => {
  const year = startedAt
    ? moment(startedAt).startOf('month').add(idx, 'months').year()
    : new Date().getFullYear();
  return { salarioMinimo: getYearValue(SALARIO_MINIMO, year), uvt: getYearValue(UVT, year) };
};

export const getPeriodDateInfo = (contract, periodDays, idx) => {
  const days = Array.isArray(periodDays) ? periodDays[idx] : null;
  if (!contract?.startedAt || !days?.length) return null;

  const month = moment(contract.startedAt).startOf('month').add(idx, 'months');
  const first = days[0];
  const last = days[days.length - 1];

  // Tramos suspendidos DENTRO de la envolvente: los huecos de la secuencia.
  // Lo que falta antes del primer día o después del último no es un hueco,
  // es el recorte que ya expresa la propia envolvente.
  const gaps = [];
  for (let i = 1; i < days.length; i++) {
    if (days[i] !== days[i - 1] + 1) gaps.push({ from: days[i - 1] + 1, to: days[i] - 1 });
  }

  const startedAt = month.clone().date(first);
  const finishedAt = month.clone().date(last);

  return {
    // Forma corta para la interfaz ("1 abr. - 19 abr.").
    range: `${startedAt.format('D MMM')} - ${finishedAt.format('D MMM')}`,
    month: month.format('MMMM YYYY').replace(/^./, (c) => c.toUpperCase()),
    // Días trabajados del periodo: los ejecutables, NO la diferencia entre
    // los extremos (que sobrecontaría un periodo discontinuo). Es la base
    // con que se prorratea el piso del IBC de la seguridad social.
    workingDays: days.length,
    startedAt,
    finishedAt,
    gaps,
    isContiguous: gaps.length === 0,
  };
};

// Novedades que tocan el periodo `idx`, para el aviso de la fila del plan de
// pagos y del modal de seguridad social. Acumula sobre `newsArr` sin duplicar
// y lo devuelve.
//
// La ventana del cruce es el MES CALENDARIO del periodo — acotado por el
// inicio del contrato en el primer periodo y por el fin vigente en el último —
// y NO sus días ejecutables: una suspensión que arranca el 20 de abril empieza
// justo donde terminan los días ejecutables de abril, así que cruzarla contra
// ellos la dejaría fuera del aviso del periodo que ella misma recortó.
export const collectPeriodNews = (contract, paymentPlan, finishedAt, idx, newsArr = []) => {
  if (!contract?.news || !Array.isArray(newsArr) || !Array.isArray(paymentPlan)) return newsArr;

  const contractStart = moment(contract.startedAt);
  const month = contractStart.clone().startOf('month').add(idx, 'months');
  const from = idx === 0 ? contractStart : month.clone().date(1);
  const to = idx === paymentPlan.length - 1 && finishedAt
    ? moment(finishedAt)
    : month.clone().date(Math.min(30, month.daysInMonth()));

  contract.news.forEach((news) => {
    const newsStartedAt = moment(news.startedAt);
    const newsFinishedAt = news.finishedAt ? moment(news.finishedAt) : null;

    const touches = newsFinishedAt
      ? (newsStartedAt.isSameOrAfter(from) && newsStartedAt.isSameOrBefore(to))
        || (newsFinishedAt.isSameOrAfter(from) && newsFinishedAt.isSameOrBefore(to))
        || (newsStartedAt.isSameOrBefore(from) && newsFinishedAt.isSameOrAfter(to))
      : newsStartedAt.isSameOrAfter(from) && newsStartedAt.isSameOrBefore(to);

    if (!touches) return;

    const already = newsArr.find((existing) => (
      existing.id === news.id
      || (existing.type === news.type
        && existing.startedAt === news.startedAt
        && existing.finishedAt === news.finishedAt)
    ));

    if (!already) newsArr.push(news);
  });

  return newsArr;
};

// ¿El contratista puede enviar el mes a revisión? Requiere al menos una
// actividad con descripción y, según el mes, la validación de seguridad social:
// - Primer periodo ejecutable: no existe periodo anterior dentro del contrato,
//   así que no se exige SS; se exigen en cambio los certificados de afiliación
//   del contrato (EPS, AFP y ARL).
// - Periodos siguientes: SS del PERIODO EJECUTABLE ANTERIOR diligenciada
//   (datos + planilla), pago a mes vencido — el llamador pasa en
//   `socialSecurityPayment` el registro `payment_{getPreviousExecutableIdx}`,
//   que salta los meses en cero.
// Devuelve el motivo del bloqueo (cadena) o '' si se puede enviar.
export const getSubmitForReviewBlockReason = ({
  activities = [], socialSecurityPayment, isFirstMonth = false, contractCertificates
} = {}) => {
  if (activities.length === 0) return 'Registra al menos una actividad';
  if (activities.some((act) => !act.description)) return 'Hay actividades sin descripción';
  if (isFirstMonth) {
    const missing = ['eps', 'afp', 'arl'].filter((key) => !contractCertificates?.[key]);
    if (missing.length > 0) {
      return `Carga en el contrato ${missing.length > 1 ? 'los certificados' : 'el certificado'} de afiliación ${missing.map((key) => key.toUpperCase()).join(', ')} antes de enviar a revisión`;
    }
    return '';
  }
  if (!isSocialSecurityFilled(socialSecurityPayment)) {
    return 'Diligencia la seguridad social del periodo anterior (aportes, planilla, fecha de pago y comprobante) antes de enviar a revisión';
  }
  return '';
};

export const getStatusColor = (status) => {
  const colors = {
    DRAFT: "default",
    SUPERVISOR: "info",
    APPROVED: "success",
    CLOSED: "warning",
    SUSPENDED: "error",
    FINISHED: "secondary",
  };
  return colors[status] || "default";
};

export const exportContractsToCSV = (filteredContracts) => {
  const headers = [
    "Código",
    "Tipo",
    "Contratista",
    "Cédula",
    "Correo",
    "Supervisor(es)",
    "Objeto",
    "Valor",
    "Duración",
    "F. Inicio",
    "F. Cierre",
    "Dependencia",
    "Naturaleza",
    "Estado",
    "Novedades - Tipo",
    "Novedades - F. Inicio",
    "Novedades - F. Fin",
    "SECOP",
    "CDP/CRP",
  ];

  const rows = filteredContracts.map((contract) => {
    const getContractFieldWithNews = getContractField(contract.news || [], {
      status: contract.status,
      finishedAt: contract.finishedAt,
      contractPeriod: contract.contractPeriod,
      contractBudget: contract.contractBudget,
      startedAt: contract.startedAt,
      supervisors: contract.supervisors,
      salary: contract.salary,
      tasks: contract.tasks,
    });

    const supervisorNames =
      contract.supervisors
        ?.map((s) => `${s.name || ""} ${s.lastName || ""}`)
        .join("; ") || "N/A";
    const newsTypes =
      contract.news?.map((n) => n.type || "N/A").join("; ") || "N/A";
    const newsStartDates =
      contract.news
        ?.map((n) =>
          n.startedAt
            ? moment(
                n.startedAt.toDate ? n.startedAt.toDate() : n.startedAt
              ).format("DD/MM/YYYY")
            : "N/A"
        )
        .join("; ") || "N/A";
    const newsEndDates =
      contract.news
        ?.map((n) =>
          n.finishedAt
            ? moment(
                n.finishedAt.toDate ? n.finishedAt.toDate() : n.finishedAt
              ).format("DD/MM/YYYY")
            : "N/A"
        )
        .join("; ") || "N/A";
    const hasSECOP = contract.annexes?.some((a) =>
      a.name?.toLowerCase().includes("secop")
    )
      ? "Sí"
      : "No";
    const hasCDP = contract.annexes?.some((a) =>
      a.name?.toLowerCase().includes("cdp y crp")
    )
      ? "Sí"
      : "No";

    return [
      contract.contractCode || "N/A",
      contract.contractType || "N/A",
      contract.contractor
        ? `${contract.contractor.name || ""} ${
            contract.contractor.lastName || ""
          }`
        : "Sin asignar",
      contract.contractorNuip || "N/A",
      contract.contractor?.email || "N/A",
      supervisorNames,
      `"${(contract.contractSubject || "N/A").replace(/"/g, '""')}"`,
      getContractFieldWithNews("contractBudget") || 0,
      getContractFieldWithNews("contractPeriod") || "N/A",
      contract.startedAt
        ? moment(
            contract.startedAt.toDate
              ? contract.startedAt.toDate()
              : contract.startedAt
          ).format("DD/MM/YYYY")
        : "N/A",
      (() => {
        const finishedAt = getContractFieldWithNews("finishedAt");
        return finishedAt
          ? moment(
              finishedAt.toDate ? finishedAt.toDate() : finishedAt
            ).format("DD/MM/YYYY")
          : "N/A";
      })(),
      contract.dependency || "N/A",
      contract.nature || "N/A",
      contract.status || "N/A",
      newsTypes,
      newsStartDates,
      newsEndDates,
      hasSECOP,
      hasCDP,
    ];
  });

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `financiamiento_contratos_${moment().format("YYYYMMDD_HHmmss")}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Req 7: exporta el log de auditoría de carga de documentos a CSV (compatible con Excel).
export const exportAuditLogsToCSV = (logs) => {
  const headers = [
    "Fecha y hora",
    "Usuario",
    "Documento",
    "Tipo documental",
    "Expediente",
    "Acción",
  ];

  const rows = (logs || []).map((log) => [
    log.timestamp ? moment(log.timestamp).format("DD/MM/YYYY HH:mm:ss") : "N/A",
    `"${(log.userName || "N/A").replace(/"/g, '""')}"`,
    `"${(log.documentName || "N/A").replace(/"/g, '""')}"`,
    `"${(log.documentType || "N/A").replace(/"/g, '""')}"`,
    `"${(log.expedientId || "N/A").replace(/"/g, '""')}"`,
    log.action || "N/A",
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  const blob = new Blob([`﻿${csvContent}`], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `auditoria_documentos_${moment().format("YYYYMMDD_HHmmss")}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function getAccountDate(startedAt, paymentPlan, finishedAt, idx) {
  if (Number.isNaN(idx)) return;

  let endedAt;
  if (idx === 0) {
    endedAt = moment(new Date(startedAt.year(), startedAt.month() + 1, 0));
  } else if (idx === paymentPlan.length - 1) {
    endedAt = finishedAt;
  } else {
    const adjStartedAt = moment(new Date(startedAt.year(), startedAt.month() + idx, 1));
    endedAt = moment(new Date(adjStartedAt.year(), adjStartedAt.month() + 1, 0));
  }

  if (endedAt.date() > 30) {
    endedAt = endedAt.clone().date(30);
  }

  return endedAt;
}

// Redacción extensa del rango para el documento impreso ("abril 1 al 19"),
// sobre las mismas fechas que rotula la interfaz en forma corta. Solo cambia
// la redacción: las fechas salen de `getPeriodDateInfo`.
const getPrintedPeriod = (info) => (
  info && `${info.startedAt.format('MMMM D')} al ${info.finishedAt.format('D')}`
);

// Cuenta aprobada con documentos: la aprobación vacía no exige seguridad
// social ni genera cuenta de cobro, así que no bloquea nada.
const isApprovedWithDocuments = (payment) => payment?.approved === true && !payment?.approvedEmpty;

// ¿La SS del propio mes ya estaba revisada cuando se aprobó su cuenta? Alguna
// marca de revisión (o la aprobación legada `socialApprovedAt`) con fecha
// anterior o igual a `approvedAt`. Sin esas fechas (registros legados) basta
// con que esté revisada, como antes de comparar.
const wasOwnSSReviewedAtApproval = (payment) => {
  if (getSocialSecurityStatus(payment) !== REVIEW_STATUS.REVIEWED) return false;
  const approvedAt = payment?.approvedAt ? moment(payment.approvedAt) : null;
  if (!approvedAt?.isValid()) return true;

  const review = payment.socialSecurityReview || {};
  // Devuelta después de aprobar: su cuenta no la bloqueaba, así que se aprobó a
  // mes vencido. Las marcas previas no cubren lo que se corrigió después.
  if ((review.returns || []).some((ret) => ret?.at && moment(ret.at).isAfter(approvedAt))) return false;

  // Aprobación legada sin fecha: no hay con qué comparar.
  const legacyApproved = payment.socialApproved === true || review.status === REVIEW_STATUS.APPROVED;
  if (legacyApproved && !payment.socialApprovedAt) return true;

  const times = [
    ...Object.values(review.reviews || {}).map((mark) => mark?.at),
    legacyApproved ? payment.socialApprovedAt : null,
  ].filter(Boolean);
  if (times.length === 0) return true;
  return times.some((at) => !moment(at).isAfter(approvedAt));
};

// Resuelve cuál registro de seguridad social aplica para la cuenta de cobro del mes `idx`.
// Regla (alineada con el reporte del PDF): si el pago de la SS (payAt) cae dentro del mismo
// mes de la cuenta de cobro, se usa el del mes actual (contratista anticipado / beneficio
// tributario); en caso contrario se usa el del periodo EJECUTABLE anterior (pago en mes
// vencido), saltando los meses que una suspensión dejó en cero.
// Devuelve { key, payment, isSameMonth }. `key` es null cuando no hay SS exigible
// (p. ej. el primer periodo con pago vencido y sin periodo anterior).
export function resolveSocialSecurityPayment(contract, idx, getContractFieldWithNews) {
  const paymentPlan = getContractFieldWithNews('paymentPlan');
  const finishedAt = getContractFieldWithNews('finishedAt');
  const accountDate = getAccountDate(contract.startedAt, paymentPlan, finishedAt, idx);

  const currentKey = `payment_${idx}`;
  const previousIdx = getPreviousExecutableIdx(paymentPlan, idx);
  const previousKey = previousIdx >= 0 ? `payment_${previousIdx}` : null;
  const currentPayment = contract.payments?.[currentKey];
  const previousPayment = previousKey ? contract.payments?.[previousKey] : null;

  const payAtMonth = currentPayment?.payAt ? moment(currentPayment.payAt).startOf('month') : null;
  const accountMonth = accountDate?.clone().startOf('month');
  // Una cuenta aprobada conserva la modalidad con que se aprobó: fue de pago
  // anticipado solo si su propia SS ya estaba revisada al aprobarla (gating en
  // ContractViewPaymentPlan). Una SS del mes registrada o revisada DESPUÉS, con
  // payAt dentro del mes (el contratista pagó dos meses en uno), no la vuelve
  // anticipada: es la del periodo anterior de la cuenta siguiente.
  const approvedAsOverdue = isApprovedWithDocuments(currentPayment)
    && !wasOwnSSReviewedAtApproval(currentPayment);
  const isSameMonth = !approvedAsOverdue
    && !!(payAtMonth && accountMonth && payAtMonth.isSame(accountMonth));

  return {
    key: isSameMonth ? currentKey : previousKey,
    payment: isSameMonth ? currentPayment : previousPayment,
    isSameMonth,
  };
}

// Índice de la cuenta de cobro aprobada que BLOQUEA la seguridad social del
// mes `idx`, o -1 si ninguna la bloquea. La usan (y la imprimen en su PDF y su
// consolidado) la cuenta del periodo ejecutable siguiente, a mes vencido, y
// la del propio mes cuando su SS fue de pago anticipado. Basta con mirar el
// siguiente: por el encadenamiento, si uno posterior está aprobado, el
// siguiente también. Bloqueada, la SS no admite deshacer la revisión,
// devolverla ni editarla; se desbloquea devolviendo esa cuenta de cobro.
export const getSocialSecurityLockIdx = (contract, idx, getContractFieldWithNews) => {
  const paymentPlan = getContractFieldWithNews('paymentPlan');
  if (!isExecutablePeriod(paymentPlan, idx)) return -1;

  const nextIdx = getNextExecutableIdx(paymentPlan, idx);
  if (nextIdx >= 0 && isApprovedWithDocuments(contract.payments?.[`payment_${nextIdx}`])) return nextIdx;

  if (
    isApprovedWithDocuments(contract.payments?.[`payment_${idx}`])
    && resolveSocialSecurityPayment(contract, idx, getContractFieldWithNews).isSameMonth
  ) return idx;

  return -1;
};

// ¿Están bloqueados los certificados de afiliación del contrato (EPS, AFP y
// ARL)? Son la "seguridad social" del primer periodo ejecutable y entran en
// su consolidado: con esa cuenta aprobada no se cargan, reemplazan ni
// eliminan hasta devolverla.
export const areAffiliationCertsLocked = (paymentPlan, payments) => {
  const firstIdx = getNextExecutableIdx(paymentPlan, -1);
  return firstIdx >= 0 && isApprovedWithDocuments(payments?.[`payment_${firstIdx}`]);
};

export function buildChargeAccountDataSource({ contract, payment, users, getContractFieldWithNews }) {
  const paymentPlan = getContractFieldWithNews('paymentPlan');
  const paymentPlanDays = getContractFieldWithNews('paymentPlanDays');
  const finishedAt = getContractFieldWithNews('finishedAt');

  const getAccountDateFn = (idx) => getAccountDate(contract.startedAt, paymentPlan, finishedAt, idx);

  const accountDate = payment
    ? getAccountDateFn(parseInt(payment.split('_')[1], 10))
    : undefined;

  // Contractor
  const contractorUser = users?.find(u => u.userId === contract.contractor?.userId);
  const contractor = contractorUser ? {
    contractorName: `${contractorUser.name} ${contractorUser.lastName}`,
    contractorNuip: contractorUser.nuip || '-',
    contractorAddress: contractorUser.address || '-',
    contractorPhone: contractorUser.phone || '-',
  } : {};

  // Supervisor
  const supervisors = getContractFieldWithNews('supervisors', accountDate);
  const supervisorUser = supervisors[0];
  // El tag `supervisorPosition` de la plantilla muestra el área del supervisor,
  // no su cargo. Se lee del perfil vigente: el snapshot del contrato puede ser
  // anterior al campo `area`.
  const liveSupervisor = users?.find(u => u.userId === supervisorUser.userId);
  const supervisor = {
    supervisorName: `${supervisorUser.name} ${supervisorUser.lastName}`,
    supervisorDependence: supervisorUser.dependence,
    supervisorPosition: liveSupervisor?.area || supervisorUser.area || '-',
    supervisorNuip: supervisorUser.nuip || '-',
  };

  // Tasks
  let tasks = getContractFieldWithNews('tasks', accountDate);
  tasks = tasks && tasks.map((t, idx) => `${idx + 1}. ${t.title}`).join(', ');
  if (tasks?.length > 3500) tasks = tasks.substring(0, 3500) + '...';

  // Activities
  let activities = contract.payments && contract.payments[payment]?.activities?.map(
    ac => {
      const cleanTitle = ac.title.replace(/^\d+\.\s*/, '');
      return `(${contract?.tasks.map(t => t.title).indexOf(cleanTitle) + 1}) ${ac.description}`;
    }
  ).sort((a, b) => a > b ? 1 : a < b ? -1 : 0).join(', ');
  if (activities?.length > 2500) activities = activities.substring(0, 2500) + '...';
  activities = activities || "";

  // Social Security
  let socialSecurity;
  if (!payment) {
    socialSecurity = {
      payroll: "--", contribution: "--", payAt: "--",
      payrollCertificate: 'NO', pensioner: 'NO'
    };
  } else {
    const idx = parseInt(payment.split('_')[1], 10);

    // El mes actual se considera "pagado" cuando su fecha de pago (payAt) cae
    // dentro del mismo mes que la cuenta de cobro: ese es el caso del
    // contratista anticipado, que paga la SS del mes durante el propio mes.
    // En ese caso se reporta la planilla del mes actual; en caso contrario
    // (vencido o sin registro) se reporta la del mes anterior.
    const { payment: lastPayment, isSameMonth } = resolveSocialSecurityPayment(contract, idx, getContractFieldWithNews);

    if (!lastPayment) {
      socialSecurity = {
        payroll: 'N/A', contribution: 'N/A', payAt: 'N/A',
        payrollCertificate: 'NO', pensioner: 'N/A',
        isSameMonth: isSameMonth ? 'SI' : 'NO'
      };
    } else {
      socialSecurity = {
        payroll: lastPayment.payroll || "--",
        contribution: (lastPayment.contribution && currency(lastPayment.contribution, { precision: 0 }).format()) || "--",
        payAt: lastPayment.payAt ? lastPayment.payAt.format('DD/MM/YYYY') : "--",
        payrollCertificate: (lastPayment.certificates?.payroll || lastPayment.certificates?.form || lastPayment.certificates?.transaction) ? 'SI' : 'NO',
        pensioner: lastPayment.certificates?.pensioner ? 'SI' : 'NO',
        isSameMonth: isSameMonth ? 'SI' : 'NO'
      };
    }
  }

  // Additional Budget
  let additionalBudget = "";
  if (payment) {
    const idx = parseInt(payment.split('_')[1], 10);
    const currentBudget = getContractFieldWithNews('contractBudget', getAccountDateFn(idx));
    additionalBudget = currency(currentBudget - contract.contractBudget, { precision: 0 }).format();
  }

  const dataSource = {
    ...contract,
    ...contractor,
    dependency: contract.dependency,
    startedAt: contract.startedAt.format('DD/MM/YYYY'),
    finishedAt: finishedAt.format('DD/MM/YYYY'),
    accountPeriod: payment && `${getPrintedPeriod(getPeriodDateInfo(contract, paymentPlanDays, parseInt(payment.split('_')[1], 10)))} de ${accountDate?.format('YYYY')}`,
    // Número de la cuenta: consecutivo de periodos ejecutables, no el índice.
    // Un mes que una suspensión dejó en cero no consume número.
    number: payment && getPeriodNumber(paymentPlan, parseInt(payment.split('_')[1], 10)),
    date_day: accountDate?.format('DD'),
    date_month: accountDate?.format('MM'),
    date_year: accountDate?.format('YYYY'),
    payment: contract.payments && contract.payments[payment],
    additionalBudget,
    tasks,
    activities,
    ...socialSecurity,
    ...(contract.payments && contract.payments[payment]?.risk ? { risk: 'X' } : { noRisk: 'X' }),
    supervisors,
    ...supervisor
  };

  return {
    dataSource,
    paymentPlan,
    getAccountDate: getAccountDateFn,
  };
}
